import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { benefitsService } from '../services/benefits';
import { storesService } from '../services/stores';
import { Search } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Toaster } from './ui/toaster';
import { API_BASE_URL } from '../config';
import { FlipCard } from './ui/FlipCard';
import VerificarCorreo from './VerificarCorreo';
import { VD } from '../lib/brand';
import { PlanSwipe } from './PlanSwipe';
import { BloqueBeneficio } from './CheckoutVondi';
import { savingsService } from '../services/savings';

const Benefits = ({ user }) => {
  const navigate = useNavigate();
  const [benefits, setBenefits]           = useState([]);
  const [stores, setStores]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [search, setSearch]               = useState('');
  /* Estado freemium del usuario: qué desbloqueó y si le queda cupo. */
  const [unlockState, setUnlockState]     = useState(null);
  const [unlocking, setUnlocking]         = useState(null);
  /*
    Beneficio cuyo desbloqueo quedó esperando la verificación de correo.

    El backend niega el beneficio gratis sin correo verificado (403
    CORREO_NO_VERIFICADO) — es el anti-abuso de los descuentos. Las cuentas
    nuevas verifican durante el registro; este modal existe para las que se
    registraron ANTES de esa regla y vienen a desbloquear ahora.
  */
  const [pidiendoCodigo, setPidiendoCodigo] = useState(null);
  /*
    El ahorro alimenta el bloque de compra: cuánto lleva y qué plan destacar.

    Se guarda el **objeto completo** que devuelve el servicio, no solo el total.
    `BloqueBeneficio` necesita `hasData` y `membership` para decidir si enseña la
    resta (ahorro − costo = ganancia) o solo la cifra; con un número suelto no
    podría, y esa resta es justo lo que Iván pidió ver aquí.
  */
  const [ahorro, setAhorro]               = useState(null);
  const [planSugerido, setPlanSugerido]   = useState('annual');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [benefitsResponse, estado] = await Promise.all([
          benefitsService.getAll(),
          benefitsService.getUnlockState(),
        ]);
        if (benefitsResponse.success && benefitsResponse.data) {
          setBenefits(benefitsResponse.data);
        } else {
          throw new Error('No se pudieron cargar los beneficios');
        }
        setUnlockState(estado);
        const allStores = await storesService.getStores();
        setStores(allStores);
      } catch (err) {
        console.error('Error cargando datos:', err);
        setError(err.message || 'Error al cargar los beneficios');
        toast({ title: 'Error', description: 'No se pudieron cargar los beneficios', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    loadData();

    savingsService
      .summary()
      .then((r) => {
        setAhorro(r?.data ?? null);
        if (r?.data?.membership?.suggestedPlan) setPlanSugerido(r.data.membership.suggestedPlan);
      })
      .catch(() => {});
  }, []);

  /**
   * Estado visual de una tarjeta (FLIPCARD_SPEC §6).
   *
   * Con membresía activa todo se ve normal. Sin ella, manda `accessType`:
   * las de muestra se pueden abrir, las premium quedan en tono tenue con
   * candado — legibles a propósito, porque son el gancho de venta.
   */
  const estadoDeTarjeta = useCallback((benefit) => {
    if (!unlockState) return 'bloqueada';
    if (unlockState.isSubscriber) return 'normal';
    if (unlockState.unlockedBenefitIds?.includes(String(benefit.id))) return 'normal';
    /*
      Mientras le quede su beneficio de bienvenida, TODAS las tarjetas se ven
      disponibles: puede elegir la que quiera (Iván, 11-ago-2026). En cuanto lo
      gasta, el resto queda bajo candado y el siguiente toque lleva al pago.
    */
    return unlockState.canUnlockMore ? 'gratis' : 'bloqueada';
  }, [unlockState]);

  /**
   * Un tap sobre una tarjeta bloqueada lleva al paywall; sobre una de muestra,
   * la desbloquea. Sobre una ya abierta, deja que la tarjeta voltee sola.
   */
  const alTocarTarjeta = useCallback(async (benefit) => {
    const estado = estadoDeTarjeta(benefit);

    if (estado === 'bloqueada') {
      navigate('/subscription', { state: { from: 'benefit-locked', benefitId: benefit.id } });
      return;
    }

    if (estado !== 'gratis') return; // ya abierta: la tarjeta voltea por su cuenta

    setUnlocking(benefit.id);
    try {
      const res = await benefitsService.unlock(benefit.id);
      setUnlockState((prev) => ({
        ...prev,
        unlockedBenefitIds: [...(prev?.unlockedBenefitIds ?? []), String(benefit.id)],
        unlocksUsed: res?.data?.unlocksUsed ?? (prev?.unlocksUsed ?? 0) + 1,
        canUnlockMore: res?.data?.canUnlockMore ?? false,
      }));
      toast({
        title: '¡Beneficio desbloqueado!',
        description: res?.message || 'Muestra tu QR en tienda para usarlo.',
      });
    } catch (err) {
      // El backend manda 402 con code PAYWALL o REQUIERE_MEMBRESIA. El cliente
      // API deja el cuerpo completo en err.data y el código HTTP en err.status.
      const codigo = err?.data?.code;
      // SIN_INVITACION: la cuenta no llegó por un enlace que dé el gratis.
      if (err?.status === 402 || codigo === 'PAYWALL' || codigo === 'REQUIERE_MEMBRESIA' || codigo === 'SIN_INVITACION') {
        navigate('/subscription', { state: { from: 'paywall', benefitId: benefit.id } });
        return;
      }
      // Falta verificar el correo: se abre el paso del código y, al pasarlo,
      // se reintenta este mismo desbloqueo.
      if (codigo === 'CORREO_NO_VERIFICADO') {
        setPidiendoCodigo(benefit);
        return;
      }
      toast({
        title: 'No se pudo desbloquear',
        description: err?.message || 'Inténtalo de nuevo en un momento.',
        variant: 'destructive',
      });
    } finally {
      setUnlocking(null);
    }
  }, [estadoDeTarjeta, navigate]);

  const getStoreLogo = (benefit) => {
    if (benefit.storeLogo) return benefit.storeLogo;
    const store = stores.find(s => s.id === benefit.storeId || s.id === String(benefit.storeId));
    if (store?.logo) {
      return store.logo.startsWith('http')
        ? store.logo
        : `${API_BASE_URL}${store.logo.startsWith('/') ? '' : '/'}${store.logo}`;
    }
    return null;
  };

  const q = search.trim().toLowerCase();
  const filteredBenefits = benefits.filter(b => {
    if (!q) return true;
    const store = stores.find(s => s.id === b.storeId || s.id === String(b.storeId));
    return (
      (b.storeName || '').toLowerCase().includes(q) ||
      (b.title || '').toLowerCase().includes(q) ||
      (b.description || '').toLowerCase().includes(q) ||
      (store?.category || '').toLowerCase().includes(q)
    );
  });

  const firstName = user?.name?.split(' ')[0] || 'Miembro';

  return (
    <>
      <style>{`.mp-noscroll::-webkit-scrollbar{display:none}`}</style>

      {/* Sticky header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        paddingTop: 62, paddingLeft: 22, paddingRight: 22, paddingBottom: 14,
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}>
        {/* ¡Bienvenido! with right-fade mask */}
        <div style={{
          overflow: 'hidden',
          maskImage: 'linear-gradient(to right, black 52%, transparent 88%)',
          WebkitMaskImage: 'linear-gradient(to right, black 52%, transparent 88%)',
          marginBottom: 14,
        }}>
          <h1 style={{
            fontSize: 'clamp(36px, 11vw, 48px)',
            fontWeight: 900,
            letterSpacing: '-0.04em',
            color: VD.tinta,
            whiteSpace: 'nowrap',
            margin: 0,
            lineHeight: 1.05,
          }}>
            ¡ Bienvenido, {firstName}!
          </h1>
        </div>

        {/* Buscador (centrado) */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 9999, padding: '8px 14px',
            width: '100%', maxWidth: 260,
          }}>
            <Search size={16} color={VD.pizarra} style={{ flexShrink: 0 }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar tienda o beneficio"
              style={{
                border: 'none', outline: 'none', background: 'transparent',
                fontSize: 13, fontWeight: 500, color: VD.tinta, width: '100%',
              }}
            />
          </div>
        </div>
      </div>

      {/*
        Gastó su beneficio de bienvenida y todavía no compra.

        Iván (13-ago-2026): «si ya ocupé mi beneficio gratis y todavía no
        compro, en el Home me tiene que seguir apareciendo todo bloqueado, la
        flip card para comprar y mi ahorro acumulado, el costo y todo».

        Antes se quedaba con el catálogo entero bajo candado y **nada que
        hacer**: ni qué cuesta, ni por dónde. El candado sin salida no vende,
        solo frustra — y este es justo el momento en que la persona ya probó el
        producto con dinero real y tiene con qué comparar.
      */}
      {!loading && unlockState && !unlockState.isSubscriber && !unlockState.canUnlockMore && (
        <div style={{ padding: '18px 22px 0' }}>
          {/*
            El ahorro dejó de ser una línea gris.

            Iván (13-ago-2026), señalando `HOME_IDEAL.jpeg` contra
            `REFERENCIA_ACTUAL.png`: «que el ahorro salga más grande, como en la
            foto». La diferencia entre las dos imágenes no es de tamaño de letra,
            es de jerarquía: en la referencia el ahorro es un renglón de 14px
            perdido sobre el botón; en la ideal es lo primero que se lee, con la
            cuenta completa debajo —lo ahorrado, lo que cuesta, lo que queda.

            Se reusa `BloqueBeneficio`, el bloque del checkout, en vez de dibujar
            otro: es exactamente esa cuenta, ya construida y ya probada, y así el
            número que ve aquí es el mismo que verá al pagar. Dos versiones del
            mismo argumento acabarían discrepando.
          */}
          <BloqueBeneficio ahorro={ahorro} />
          <PlanSwipe planSugerido={planSugerido} onElegir={() => navigate('/subscription')} />
        </div>
      )}

      {/* Content */}
      <div style={{ paddingTop: 20, paddingBottom: 110 }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '64px 0', color: VD.pizarra }}>
            Cargando beneficios...
          </div>
        )}

        {error && !loading && (
          <div style={{ textAlign: 'center', padding: '64px 0', color: '#9C2A1F' }}>
            {error}
          </div>
        )}

        {!loading && !error && filteredBenefits.length === 0 && (
          <div style={{ textAlign: 'center', padding: '64px 0', color: VD.pizarra }}>
            {q ? 'No se encontraron beneficios para tu búsqueda' : 'No hay beneficios disponibles'}
          </div>
        )}

        {/* Lista de flip cards.
            El detalle y los T&C ya no viven en un modal: están en el reverso de
            la tarjeta, que ahora sí voltea. */}
        {!loading && !error && filteredBenefits.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, padding: '0 22px' }}>
            {filteredBenefits.map((benefit) => {
              const estado = estadoDeTarjeta(benefit);
              /* Las tarjetas abiertas voltean para enseñar sus T&C; las demás
                 usan el tap para desbloquear o para llevar al paywall. */
              const abierta = estado === 'normal';
              return (
                <FlipCard
                  key={benefit.id}
                  estado={estado}
                  onClick={abierta ? undefined : alTocarTarjeta}
                  style={unlocking === benefit.id ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
                  benefit={{
                    ...benefit,
                    // El logo del aliado ya viene resuelto de la API; este respaldo
                    // cubre las tiendas cuyo logo solo llega por GET /api/stores.
                    allyLogo: benefit.allyLogo || getStoreLogo(benefit),
                  }}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Verificación de correo pendiente: mismo componente que en el registro,
          aquí como modal. `autoEnviar`: el código del alta de estas cuentas
          nunca existió o caducó hace mucho, así que se manda uno al abrir. */}
      {pidiendoCodigo && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 50,
            background: 'rgba(15,15,15,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 22,
          }}
          onClick={() => setPidiendoCodigo(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 28, padding: '28px 22px',
              width: '100%', maxWidth: 400, maxHeight: '90vh', overflowY: 'auto',
            }}
          >
            <VerificarCorreo
              correo={user?.email}
              autoEnviar
              onVerificado={() => {
                const beneficio = pidiendoCodigo;
                setPidiendoCodigo(null);
                setUnlockState((prev) => (prev ? { ...prev, emailVerified: true } : prev));
                // Reintentar el desbloqueo que quedó esperando el código.
                alTocarTarjeta(beneficio);
              }}
              onCancelar={() => setPidiendoCodigo(null)}
            />
          </div>
        </div>
      )}

      <Toaster />
    </>
  );
};

export default Benefits;
